import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, Box, TextField, Typography, InputAdornment, Chip, CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useNavigate } from "react-router-dom";

const API_BASE = "/api";

function authHeaders() {
  const userId = localStorage.getItem("user_id");
  const h = { "Content-Type": "application/json" };
  if (userId) h["X-User-Id"] = String(userId);
  return h;
}

/**
 * Command Palette — atajo de teclado Cmd+K (Mac) / Ctrl+K (Windows/Linux)
 * que abre un buscador instantáneo de rutas del menú.
 *
 * Props:
 *   - commands: [{ label, path, group?, icon?, keywords? }]  — lista buscable
 *
 * Búsqueda: case-insensitive + accent-insensitive en label, group y keywords.
 * Navegación con flechas ↑↓, Enter para seleccionar, Esc para cerrar.
 */

function normalize(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export default function CommandPalette({ commands }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [pacientes, setPacientes] = useState([]);
  const [citas, setCitas] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Atajo global Cmd+K / Ctrl+K
  useEffect(() => {
    function onKey(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset al abrir + cargar citas de hoy en background
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Focus después de que el dialog termine de animar
      setTimeout(() => inputRef.current?.focus(), 50);
      // Pre-cargar citas de hoy para que aparezcan al abrir sin teclear
      fetch(`${API_BASE}/emr/citas/hoy`, { headers: authHeaders() })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => setCitas(d?.citas || []))
        .catch(() => setCitas([]));
    } else {
      setPacientes([]);
    }
  }, [open]);

  // Debounce de la query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 220);
    return () => clearTimeout(t);
  }, [query]);

  // Buscar pacientes (con debounce, mínimo 2 chars)
  useEffect(() => {
    if (!open) return;
    if (debouncedQuery.length < 2) {
      setPacientes([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    fetch(
      `${API_BASE}/emr/pacientes?buscar=${encodeURIComponent(debouncedQuery)}&limit=5`,
      { headers: authHeaders() },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return;
        setPacientes(d?.pacientes || []);
      })
      .catch(() => { if (!cancelled) setPacientes([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, open]);

  // Filtrar comandos (rutas del menú)
  const cmdsFiltered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return commands;
    return commands.filter((c) => {
      const haystack = [c.label, c.group || "", ...(c.keywords || [])]
        .map(normalize)
        .join(" ");
      return q.split(/\s+/).every((token) => haystack.includes(token));
    });
  }, [query, commands]);

  // Filtrar citas locales por nombre del paciente
  const citasFiltered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return citas;
    return citas.filter((c) =>
      normalize(c.paciente_nombre || "").includes(q) ||
      normalize(c.motivo || "").includes(q),
    );
  }, [query, citas]);

  // Construir lista plana con secciones para navegación con flechas
  const flatItems = useMemo(() => {
    const out = [];
    if (cmdsFiltered.length > 0) {
      out.push({ kind: "header", label: "Páginas" });
      cmdsFiltered.forEach((c) => out.push({ kind: "command", data: c }));
    }
    if (pacientes.length > 0) {
      out.push({ kind: "header", label: "Pacientes" });
      pacientes.forEach((p) => out.push({ kind: "paciente", data: p }));
    }
    if (citasFiltered.length > 0) {
      out.push({ kind: "header", label: "Citas de hoy" });
      citasFiltered.forEach((c) => out.push({ kind: "cita", data: c }));
    }
    return out;
  }, [cmdsFiltered, pacientes, citasFiltered]);

  // Índices "navegables" (sin headers)
  const navigableIdxs = useMemo(
    () => flatItems.map((it, i) => ({ it, i })).filter(({ it }) => it.kind !== "header").map(({ i }) => i),
    [flatItems],
  );

  const handleSelect = (item) => {
    if (!item) return;
    if (item.kind === "command") navigate(item.data.path);
    else if (item.kind === "paciente") navigate(`/emr/paciente/${item.data.id}`);
    else if (item.kind === "cita") {
      // Abrir agenda del día (la cita está allí). Si tienes una pantalla de
      // detalle de cita específica, cámbialo a esa ruta.
      navigate("/agenda");
    }
    setOpen(false);
  };

  // Activar primer item navegable cuando cambia el set
  useEffect(() => {
    if (navigableIdxs.length === 0) return;
    if (!navigableIdxs.includes(activeIdx)) {
      setActiveIdx(navigableIdxs[0]);
    }
  }, [navigableIdxs, activeIdx]);

  // Scroll al item activo si se sale de vista
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  function handleKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const pos = navigableIdxs.indexOf(activeIdx);
      const next = pos < 0 ? navigableIdxs[0] : navigableIdxs[Math.min(pos + 1, navigableIdxs.length - 1)];
      if (next !== undefined) setActiveIdx(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const pos = navigableIdxs.indexOf(activeIdx);
      const prev = pos < 0 ? navigableIdxs[0] : navigableIdxs[Math.max(pos - 1, 0)];
      if (prev !== undefined) setActiveIdx(prev);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(flatItems[activeIdx]);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          mt: { xs: 4, md: 8 },
          alignSelf: "flex-start",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)",
          maxHeight: "70vh",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar páginas, secciones, herramientas…"
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
            sx: { fontSize: 16 },
          }}
        />
      </Box>

      <Box
        ref={listRef}
        sx={{ maxHeight: 420, overflowY: "auto", py: 0.5 }}
      >
        {flatItems.length === 0 && !searching ? (
          <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>
            <Typography sx={{ fontSize: 14 }}>
              {query ? `Sin coincidencias para "${query}"` : "Empieza a escribir para buscar"}
            </Typography>
          </Box>
        ) : (
          <>
            {flatItems.map((item, idx) => {
              if (item.kind === "header") {
                return (
                  <Typography
                    key={`hdr-${idx}-${item.label}`}
                    sx={{
                      px: 2, pt: 1.5, pb: 0.4,
                      fontSize: 10, fontWeight: 700, color: "#94a3b8",
                      textTransform: "uppercase", letterSpacing: 0.6,
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              }
              const isActive = idx === activeIdx;
              return (
                <Box
                  key={`item-${idx}-${item.kind}`}
                  data-cmd-idx={idx}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 2, py: 1,
                    cursor: "pointer",
                    bgcolor: isActive ? "#eff6ff" : "transparent",
                    borderLeft: isActive ? "3px solid #1e3a5f" : "3px solid transparent",
                    pl: isActive ? 1.6 : 2,
                    transition: "background 0.1s",
                  }}
                >
                  <ItemIcon item={item} isActive={isActive} />
                  <ItemBody item={item} isActive={isActive} query={query} />
                  {isActive && (
                    <Chip
                      size="small"
                      icon={<KeyboardReturnIcon sx={{ fontSize: 13 }} />}
                      label="Enter"
                      sx={{ height: 20, fontSize: 10, bgcolor: "#1e3a5f", color: "#fff" }}
                    />
                  )}
                </Box>
              );
            })}
            {searching && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, color: "#94a3b8" }}>
                <CircularProgress size={12} />
                <Typography sx={{ fontSize: 12 }}>Buscando pacientes…</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{
        px: 2, py: 0.8, borderTop: "1px solid #e2e8f0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        bgcolor: "#f8fafc",
      }}>
        <Box sx={{ display: "flex", gap: 1.5, fontSize: 11, color: "#64748b" }}>
          <span><Kbd>↑</Kbd><Kbd>↓</Kbd> navegar</span>
          <span><Kbd>↵</Kbd> ir</span>
          <span><Kbd>esc</Kbd> cerrar</span>
        </Box>
        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
          {navigableIdxs.length} resultado{navigableIdxs.length === 1 ? "" : "s"}
        </Typography>
      </Box>
    </Dialog>
  );
}

function ItemIcon({ item, isActive }) {
  let icon, bgColor;
  if (item.kind === "command") {
    icon = item.data.icon;
    bgColor = isActive ? "#1e3a5f" : "#e2e8f0";
  } else if (item.kind === "paciente") {
    icon = <PersonIcon />;
    bgColor = isActive ? "#10b981" : "#d1fae5";
  } else if (item.kind === "cita") {
    icon = <CalendarMonthIcon />;
    bgColor = isActive ? "#0ea5e9" : "#dbeafe";
  }
  if (!icon) return null;
  return (
    <Box sx={{
      width: 28, height: 28, borderRadius: 1, bgcolor: bgColor,
      color: isActive ? "#fff" : "#64748b",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
      "& svg": { fontSize: 16 },
    }}>
      {icon}
    </Box>
  );
}

function ItemBody({ item, isActive }) {
  if (item.kind === "command") {
    const cmd = item.data;
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13.5,
          fontWeight: isActive ? 700 : 500,
          color: isActive ? "#0f172a" : "#1e293b",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {cmd.label}
        </Typography>
        {cmd.group && (
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{cmd.group}</Typography>
        )}
      </Box>
    );
  }
  if (item.kind === "paciente") {
    const p = item.data;
    const sub = [
      p.fecha_nacimiento ? `Nac. ${String(p.fecha_nacimiento).slice(0, 10)}` : null,
      p.sexo,
      p.telefono,
    ].filter(Boolean).join(" · ");
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13.5,
          fontWeight: isActive ? 700 : 500,
          color: isActive ? "#0f172a" : "#1e293b",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {p.nombre_completo || "(sin nombre)"}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{sub}</Typography>
        )}
      </Box>
    );
  }
  if (item.kind === "cita") {
    const c = item.data;
    const hora = c.fecha_cita
      ? new Date(c.fecha_cita).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
      : "";
    const estado = c.estado || "pendiente";
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13.5,
          fontWeight: isActive ? 700 : 500,
          color: isActive ? "#0f172a" : "#1e293b",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {hora ? `${hora} — ` : ""}{c.paciente_nombre || "(sin paciente)"}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
          {c.motivo ? `${c.motivo} · ` : ""}{estado}
        </Typography>
      </Box>
    );
  }
  return null;
}

function Kbd({ children }) {
  return (
    <Box component="kbd" sx={{
      display: "inline-block", px: 0.6, py: 0.05, mx: 0.2,
      borderRadius: 0.6, border: "1px solid #cbd5e1",
      bgcolor: "#fff", color: "#475569",
      fontSize: 10, fontFamily: "inherit", lineHeight: 1.4,
    }}>
      {children}
    </Box>
  );
}
